import { generateMaze } from '../game/world/maze-gen'

/** Tile types in the maze grid. */
export enum TileType {
  Wall = 0,
  Path = 1,
  Dot = 2,
  PowerPellet = 3,
  Empty = 4,
  Spawn = 5,
}

/**
 * Classic Pac-Man-inspired maze layout (29 rows x 28 cols).
 * 0=Wall, 1=Path, 2=Dot, 3=PowerPellet, 4=Empty, 5=Spawn
 */
export const MAZE_LAYOUT: TileType[][] = [
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  [0,2,2,2,2,2,2,2,2,2,2,2,2,0,0,2,2,2,2,2,2,2,2,2,2,2,2,0],
  [0,2,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,2,0],
  [0,3,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,3,0],
  [0,2,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,2,0],
  [0,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,0],
  [0,2,0,0,0,0,2,0,0,2,0,0,0,0,0,0,0,0,2,0,0,2,0,0,0,0,2,0],
  [0,2,0,0,0,0,2,0,0,2,0,0,0,0,0,0,0,0,2,0,0,2,0,0,0,0,2,0],
  [0,2,2,2,2,2,2,0,0,2,2,2,2,0,0,2,2,2,2,0,0,2,2,2,2,2,2,0],
  [0,0,0,0,0,0,2,0,0,0,0,0,1,0,0,1,0,0,0,0,0,2,0,0,0,0,0,0],
  [4,4,4,4,4,0,2,0,0,0,0,0,1,0,0,1,0,0,0,0,0,2,0,4,4,4,4,4],
  [4,4,4,4,4,0,2,0,0,1,1,1,1,1,1,1,1,1,1,0,0,2,0,4,4,4,4,4],
  [0,0,0,0,0,0,2,0,0,1,0,0,0,4,4,0,0,0,1,0,0,2,0,0,0,0,0,0],
  [1,1,1,1,1,1,2,1,1,1,0,4,4,4,4,4,4,0,1,1,1,2,1,1,1,1,1,1],
  [0,0,0,0,0,0,2,0,0,1,0,4,4,5,5,4,4,0,1,0,0,2,0,0,0,0,0,0],
  [4,4,4,4,4,0,2,0,0,1,0,0,0,0,0,0,0,0,1,0,0,2,0,4,4,4,4,4],
  [4,4,4,4,4,0,2,0,0,1,1,1,1,1,1,1,1,1,1,0,0,2,0,4,4,4,4,4],
  [0,0,0,0,0,0,2,0,0,1,0,0,0,0,0,0,0,0,1,0,0,2,0,0,0,0,0,0],
  [0,2,2,2,2,2,2,2,2,2,2,2,2,0,0,2,2,2,2,2,2,2,2,2,2,2,2,0],
  [0,2,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,2,0],
  [0,2,0,0,0,0,2,0,0,0,0,0,2,0,0,2,0,0,0,0,0,2,0,0,0,0,2,0],
  [0,3,2,2,0,0,2,2,2,2,2,2,2,1,1,2,2,2,2,2,2,2,0,0,2,2,3,0],
  [0,0,0,2,0,0,2,0,0,2,0,0,0,0,0,0,0,0,2,0,0,2,0,0,2,0,0,0],
  [0,0,0,2,0,0,2,0,0,2,0,0,0,0,0,0,0,0,2,0,0,2,0,0,2,0,0,0],
  [0,2,2,2,2,2,2,0,0,2,2,2,2,0,0,2,2,2,2,0,0,2,2,2,2,2,2,0],
  [0,2,0,0,0,0,0,0,0,0,0,0,2,0,0,2,0,0,0,0,0,0,0,0,0,0,2,0],
  [0,2,0,0,0,0,0,0,0,0,0,0,2,0,0,2,0,0,0,0,0,0,0,0,0,0,2,0],
  [0,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,0],
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
]

/** A full maze grid. All layouts share the same dimensions as MAZE_LAYOUT (29x28). */
export type Layout = TileType[][]

/**
 * The layout for a level (1-based). Level 1 is the hand-authored MAZE_LAYOUT; levels 2+
 * are deterministically generated (skeleton preserved, interior varied + connectivity-
 * validated) so each level is a distinct, always-playable arrangement.
 */
export function getLayout(level: number): Layout {
  if (level <= 1) return MAZE_LAYOUT
  return generateMaze(level) as Layout
}

/** Get maze dimensions. */
export function getMazeDimensions(): { rows: number; cols: number } {
  return { rows: MAZE_LAYOUT.length, cols: MAZE_LAYOUT[0].length }
}

/** Get tile type at grid coordinates. Returns Wall for out-of-bounds. */
export function getTileAt(row: number, col: number): TileType {
  if (row < 0 || row >= MAZE_LAYOUT.length || col < 0 || col >= MAZE_LAYOUT[0].length) {
    return TileType.Wall
  }
  return MAZE_LAYOUT[row][col]
}

/** Get all walkable (non-wall) tile positions. */
export function getWalkableTiles(): { row: number; col: number; type: TileType }[] {
  const tiles: { row: number; col: number; type: TileType }[] = []
  for (let row = 0; row < MAZE_LAYOUT.length; row++) {
    for (let col = 0; col < MAZE_LAYOUT[0].length; col++) {
      const type = MAZE_LAYOUT[row][col]
      if (type !== TileType.Wall) {
        tiles.push({ row, col, type })
      }
    }
  }
  return tiles
}

/** Find spawn positions in the maze. */
export function getSpawnPositions(): { row: number; col: number }[] {
  const spawns: { row: number; col: number }[] = []
  for (let row = 0; row < MAZE_LAYOUT.length; row++) {
    for (let col = 0; col < MAZE_LAYOUT[0].length; col++) {
      if (MAZE_LAYOUT[row][col] === TileType.Spawn) {
        spawns.push({ row, col })
      }
    }
  }
  return spawns
}
